"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Mail, Code, Zap, ShieldCheck, Rocket, Users } from "lucide-react";
import { motion, AnimatePresence, type Variants } from "framer-motion";


const keyFeatures = [
  { icon: <Code size={22} />, text: "Creative & Sophisticated Web Designs" },
  { icon: <Zap size={22} />, text: "Lightning-Fast Performance" },
  { icon: <ShieldCheck size={22} />, text: "Rock-Solid Security" },
  { icon: <Rocket size={22} />, text: "Future-Ready Scalability" },
  { icon: <Users size={22} />, text: "Visually stunning and strategically built websites tailored to elevate your brand" },
];

const slides = [
  {
    badge: "Websites that work as hard as you do",
    pre: "Beautiful websites ",
    accent: "built to convert",
    post: " — not just impress",
    sub: "Your website is your best salesperson. I build fast, polished, mobile-ready sites that turn visitors into customers — no fluff, just results.",
  },
  {
    badge: "Sell online without the headaches",
    pre: "E-commerce stores ",
    accent: "built to sell",
    post: " from day one",
    sub: "From product pages to checkout to payments — I build online shops that are smooth to use, secure to buy from, and easy for you to manage.",
  },
  {
    badge: "Your data is safe with us",
    pre: "Security ",
    accent: "baked in",
    post: ", not bolted on",
    sub: "Every site I build follows industry best practices — encrypted connections, protected logins, and code that doesn't leave the door open for hackers.",
  },
  {
    badge: "More than a pretty front end",
    pre: "Powerful ",
    accent: "backends",
    post: " behind every product",
    sub: "Databases, APIs, user accounts, automation — I handle all the behind-the-scenes engineering so your product actually works at scale.",
  },
];

const INTERVAL = 4200;

export default function Hero() {
  const [index, setIndex] = useState(0);


  const goTo = useCallback((i: number) => {
    setIndex(i);
  }, []);

  // Timer resets whenever index changes, preventing rapid auto-skips after a manual click
  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [index]);

  const slide = slides[index];

  // Animation variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const featureVariants: Variants = {
    hidden: { opacity: 0, x: 30 },
    show: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", delay: i * 0.15 },
    }),
  };

  return (
    <>
      <section className="relative flex min-h-[calc(100vh-64px)] w-full items-center overflow-hidden">
      {/* ── Content — two columns ─────────────────────────────────── */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center gap-10 px-4 py-24 sm:px-8 lg:flex-row lg:items-center lg:justify-between">

        {/* Left: No background, text directly on image */}
        <div className="relative w-full max-w-2xl px-2 md:px-0">
          {/* ── Badge: fine font, inside hero ── */}
          <AnimatePresence mode="wait">
            <motion.p
              key={`badge-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }}
              exit={{ opacity: 0, y: -10, transition: { duration: 0.2 } }}
              className="mb-6 text-left text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]"
            >
              {slide.badge}
            </motion.p>
          </AnimatePresence>
          <div className="flex min-h-[300px] flex-col justify-center">
            {/* mode="wait" ensures old text leaves before new text enters */}
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                variants={containerVariants}
                initial="hidden"
                animate="show"
                exit="exit"
                className="flex flex-col"
              >
                {/* Heading */}
                <motion.h1
                  variants={itemVariants}
                  className="text-left text-4xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl leading-tight drop-shadow-md"
                >
                  {slide.pre}
                  <span className="text-[var(--accent)] drop-shadow-md">{slide.accent}</span>
                  {slide.post}
                </motion.h1>

                {/* Sub-heading */}
                <motion.p
                  variants={itemVariants}
                  className="mt-6 text-left text-base font-medium leading-relaxed text-[var(--foreground-muted)] sm:text-lg lg:text-xl drop-shadow-md max-w-xl"
                >
                  {slide.sub}
                </motion.p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* CTAs (Outside AnimatePresence so they stay pinned and don't flicker) */}
          <div className="mt-8 grid grid-cols-1 place-items-center gap-4 sm:flex sm:flex-wrap sm:justify-start">
            <Link
              href="/work"
              className="group relative flex w-full max-w-[280px] items-center justify-center gap-2 overflow-hidden rounded-full bg-[var(--accent)] px-8 py-4 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[var(--accent)]/30 active:translate-y-0 sm:inline-flex sm:w-auto sm:max-w-none"
            >
              <span className="relative z-10">See My Work</span>
              <ArrowRight
                size={16}
                className="relative z-10 transition-transform duration-200 group-hover:translate-x-1"
              />
              <div className="absolute inset-0 z-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 transition-opacity duration-500 group-hover:animate-shimmer group-hover:opacity-100" />
            </Link>
            <Link
              href="/contact"
              className="flex w-full max-w-[280px] items-center justify-center gap-2 rounded-full bg-[var(--background)] px-8 py-4 text-sm font-bold text-[var(--foreground)] shadow-md transition-all hover:text-[var(--accent)] sm:inline-flex sm:w-auto sm:max-w-none"
            >
              <Mail size={16} />
              Get in Touch
            </Link>
          </div>

          {/* Slide indicators */}
          <div className="mt-10 flex items-center justify-center gap-3 sm:justify-start">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index
                    ? "w-8 bg-[var(--accent)] shadow-[0_0_10px_var(--accent)]"
                    : "w-2 bg-[var(--foreground-muted)] hover:bg-[var(--foreground)]"
                }`}
              />
            ))}
          </div>

          {/* Mobile-only key features — shown below CTAs, hidden on desktop */}
          <div className="mt-10 flex flex-col gap-3 lg:hidden">
            {keyFeatures.map((feature, i) => (
              <motion.div
                key={feature.text}
                custom={i}
                variants={featureVariants}
                initial="hidden"
                animate="show"
                className="flex items-center gap-4 rounded-xl border border-[var(--border)] bg-[var(--background)] px-5 py-3.5 shadow-md"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)]">
                  {feature.icon}
                </span>
                <span className="text-sm font-bold text-[var(--foreground)]">
                  {feature.text}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right: key features (proper backgrounds per item) */}
        <div className="hidden w-full max-w-sm shrink-0 flex-col gap-4 lg:flex xl:max-w-md">
          {keyFeatures.map((feature, i) => (
            <motion.div
              key={feature.text}
              custom={i}
              variants={featureVariants}
              initial="hidden"
              animate="show"
              className="flex items-center gap-4 rounded-xl bg-[var(--background)] px-6 py-4 shadow-xl border border-[var(--border)]"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)]">
                {feature.icon}
              </span>
              <span className="text-sm font-bold text-[var(--foreground)]">
                {feature.text}
              </span>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
    </>
  );
}